import React, { useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';

interface CrucibleProps {
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
  /** Temperature in °C — controls red-glow emissive intensity. */
  temperatureC?: number;
  /** Mass of unreacted magnesium ribbon coil inside (grams). */
  contentMgG?: number;
  /** Mass of magnesium oxide (white powder) inside (grams). */
  contentMgOG?: number;
  /** Maximum Mg that fits in the bowl visually (default 0.36 g). */
  mgCapacityG?: number;
  /** Show the burning Mg with bright white emissive. */
  isBurning?: boolean;
}

/**
 * Porcelain crucible — small heat-resistant bowl used for combustion
 * reactions like the empirical-formula-of-magnesium-oxide experiment.
 * The bowl emissive ramps from cool grey-white at room temp through dull
 * red around 500 °C to bright orange near 900 °C.
 */
export function Crucible({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = 1,
  temperatureC = 25,
  contentMgG = 0,
  contentMgOG = 0,
  mgCapacityG = 0.36,
  isBurning = false,
}: CrucibleProps) {
  const bodyRef = useRef<THREE.MeshStandardMaterial>(null);
  const ribbonRef = useRef<THREE.Mesh>(null);

  const radiusTop = 0.075;
  const radiusBottom = 0.045;
  const bodyHeight = 0.085;
  const wallThickness = 0.006;

  useFrame((state) => {
    if (bodyRef.current) {
      const t = Math.max(0, Math.min(1, (temperatureC - 350) / 600));
      const flicker = isBurning ? Math.sin(state.clock.elapsedTime * 18) * 0.06 : 0;
      const intensity = t * (1.1 + flicker);
      bodyRef.current.emissiveIntensity = intensity;
      const r = 0.85 + 0.15 * t;
      const g = 0.15 + 0.55 * t;
      const b = 0.05 + 0.15 * t;
      bodyRef.current.emissive.setRGB(r, g, b);
    }
    if (ribbonRef.current && isBurning) {
      const mat = ribbonRef.current.material as THREE.MeshStandardMaterial;
      if (mat) {
        const pulse = 0.7 + Math.sin(state.clock.elapsedTime * 25) * 0.25;
        mat.emissiveIntensity = pulse;
      }
    }
  });

  const mgFraction = Math.max(0, Math.min(1, contentMgG / Math.max(0.0001, mgCapacityG)));
  const mgOFraction = Math.max(0, Math.min(1, contentMgOG / (mgCapacityG * (40 / 24))));
  const powderRadius = (radiusTop - wallThickness) * (0.5 + 0.5 * mgOFraction);
  const powderHeight = 0.012 + 0.018 * mgOFraction;
  const ribbonHeight = 0.022 + 0.018 * mgFraction;
  const ribbonRadius = 0.018 + 0.012 * mgFraction;

  return (
    <group position={position} rotation={rotation} scale={scale}>
      {/* Outer body */}
      <mesh castShadow receiveShadow>
        <cylinderGeometry args={[radiusTop, radiusBottom, bodyHeight, 36, 1, true]} />
        <meshStandardMaterial
          ref={bodyRef}
          color="#efe6d6"
          roughness={0.62}
          metalness={0.02}
          emissive="#ff5520"
          emissiveIntensity={0}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Bottom disc */}
      <mesh position={[0, -bodyHeight / 2 + 0.003, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[radiusBottom, radiusBottom, 0.006, 24]} />
        <meshStandardMaterial color="#efe6d6" roughness={0.65} metalness={0.02} />
      </mesh>

      {/* Inner cavity wall (slightly darker) */}
      <mesh>
        <cylinderGeometry args={[radiusTop - wallThickness, radiusBottom - wallThickness, bodyHeight - 0.002, 36, 1, true]} />
        <meshStandardMaterial color="#d8cbb2" roughness={0.85} side={THREE.BackSide} />
      </mesh>

      {/* Rim torus */}
      <mesh position={[0, bodyHeight / 2, 0]} rotation={[Math.PI / 2, 0, 0]} castShadow>
        <torusGeometry args={[radiusTop, 0.004, 10, 36]} />
        <meshStandardMaterial color="#e3d6bc" roughness={0.6} metalness={0.05} />
      </mesh>

      {/* Pouring lip notch */}
      <mesh position={[0, bodyHeight / 2 + 0.002, radiusTop + 0.001]} rotation={[Math.PI / 4, 0, 0]}>
        <coneGeometry args={[0.014, 0.018, 16, 1, true, 0, Math.PI]} />
        <meshStandardMaterial color="#e3d6bc" roughness={0.6} side={THREE.DoubleSide} />
      </mesh>

      {/* Magnesium ribbon coil inside the bowl */}
      {contentMgG > 0.001 && (
        <mesh ref={ribbonRef} position={[0, -bodyHeight / 2 + 0.006 + ribbonHeight / 2, 0]}>
          <torusGeometry args={[ribbonRadius, 0.004, 8, 28]} />
          <meshStandardMaterial
            color={isBurning ? '#ffffff' : '#cfd6dc'}
            metalness={isBurning ? 0.2 : 0.85}
            roughness={isBurning ? 0.2 : 0.35}
            emissive={isBurning ? '#ffffff' : '#000000'}
            emissiveIntensity={isBurning ? 1.0 : 0}
          />
        </mesh>
      )}

      {/* White MgO powder pile */}
      {contentMgOG > 0.001 && (
        <mesh position={[0, -bodyHeight / 2 + 0.006 + powderHeight / 2, 0]}>
          <coneGeometry args={[powderRadius, powderHeight, 28]} />
          <meshStandardMaterial color="#f9f9fa" roughness={0.95} metalness={0.02} />
        </mesh>
      )}
    </group>
  );
}

export default Crucible;
